import React from 'react';
import { SharedLoadoutImportCandidate } from '../core/utilities/loadout-sharing';
import Button from './Button';
import './SharedLoadoutImportModal.css';

interface SharedLoadoutImportModalProps {
    candidate: SharedLoadoutImportCandidate;
    canImportAsArmory: boolean;
    manualMissingSpoilerTags: string[];
    armoryMissingSpoilerTags: string[];
    onAddManual: () => void;
    onReplaceManual: () => void;
    onAddArmory: () => void;
    onReplaceArmory: () => void;
    onDismiss: () => void;
}

export const SharedLoadoutImportModal: React.FC<SharedLoadoutImportModalProps> = ({
    candidate,
    canImportAsArmory,
    manualMissingSpoilerTags,
    armoryMissingSpoilerTags,
    onAddManual,
    onReplaceManual,
    onAddArmory,
    onReplaceArmory,
    onDismiss,
}) => {
    const sourceLabel = candidate.inputSource === 'armory' ? 'Armory' : 'Manual';
    const actionGroups = [
        ...(canImportAsArmory
            ? [
                  {
                      key: 'armory',
                      title: 'Armory Loadouts',
                      missingSpoilerTags: armoryMissingSpoilerTags,
                      addLabel: 'Add to Armory Loadouts',
                      replaceLabel: 'Replace Current Armory Loadouts',
                      onAdd: onAddArmory,
                      onReplace: onReplaceArmory,
                  },
              ]
            : []),
        {
            key: 'manual',
            title: 'Manual Loadouts',
            missingSpoilerTags: manualMissingSpoilerTags,
            addLabel: 'Add to Manual Loadouts',
            replaceLabel: 'Replace Current Manual Loadouts',
            onAdd: onAddManual,
            onReplace: onReplaceManual,
        },
    ];
    const orderedActionGroups =
        candidate.inputSource === 'armory'
            ? actionGroups
            : [...actionGroups].sort((left, right) => {
                  const priority = left.key === 'manual' ? -1 : right.key === 'manual' ? 1 : 0;
                  return priority;
              });

    const renderSpoilerWarning = (missingSpoilerTags: string[]) => {
        if (missingSpoilerTags.length === 0) {
            return null;
        }

        return (
            <p className="shared-loadout-warning">
                Continuing will enable spoiler tags: {missingSpoilerTags.join(', ')}.
            </p>
        );
    };

    return (
        <div className="shared-loadout-overlay" onClick={onDismiss}>
            <div
                aria-labelledby="shared-loadout-title"
                aria-modal="true"
                className="shared-loadout-modal"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
            >
                <p className="workspace-card-title">Shared Loadout</p>
                <h2 className="shared-loadout-title" id="shared-loadout-title">
                    Import {candidate.title}?
                </h2>
                <p className="shared-loadout-body">
                    This share came from {sourceLabel}. Manual import is always available, and armory import is offered when the shared recipe resolves cleanly in your library.
                </p>
                <div className="shared-loadout-meta">
                    <span className="shared-loadout-badge">Source: {sourceLabel}</span>
                    {candidate.meta?.sharedAt && <span className="shared-loadout-badge">Shared: {candidate.meta.sharedAt}</span>}
                </div>
                <div className="shared-loadout-actions">
                    {orderedActionGroups.map((group) => (
                        <div key={group.key} className="shared-loadout-action-group">
                            <p className="shared-loadout-action-title">{group.title}</p>
                            {renderSpoilerWarning(group.missingSpoilerTags)}
                            <Button className="shared-loadout-button" variant="accent" onClick={group.onAdd}>
                                {group.addLabel}
                            </Button>
                            <Button className="shared-loadout-button" variant="secondary" onClick={group.onReplace}>
                                {group.replaceLabel}
                            </Button>
                        </div>
                    ))}
                    <Button className="shared-loadout-button shared-loadout-button-subtle" variant="ghost" onClick={onDismiss}>
                        Dismiss
                    </Button>
                </div>
            </div>
        </div>
    );
};
